import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { fetchCommentsGateway, toThread } from './fetchCommentsGateway';
import type { WireComment } from './mappers';
import type { CommentsGateway } from '../ports/commentsGateway';

import { FIVE_MINUTES } from '@/constants/time';
import { userScoped } from '@/util/userScope';

/**
 * A post's thread. The key is pinned by `queryOptions.keys.test.ts`: the Library
 * seeds this cache from a post read and the hooks patch it by key. It carries
 * the effective user ID because each comment says whether that reader liked it.
 */
export const getCommentsV2QueryOptions = userScoped(
  'viewer',
  (userId: string | undefined, postId: number, gateway: CommentsGateway = fetchCommentsGateway) =>
    queryOptions({
      queryKey: ['commentsV2', userId, postId],
      queryFn: () => gateway.listComments(postId),
      /**
       * Held long enough that paging back through posts already read costs
       * nothing. Writes invalidate the thread, so a new comment still lands
       * immediately rather than waiting this out.
       */
      staleTime: FIVE_MINUTES,
      gcTime: FIVE_MINUTES,
    })
);

/**
 * Puts the comments a post read carried into the thread's cache, so the thread
 * finds them fresh rather than fetching.
 *
 * Best effort: shaping them signs their authors' avatars, which is another
 * request, and a failure there must not cost the reader a post that loaded,
 * nor hold it back through retry backoff. Left unseeded, the thread fetches
 * them on its own.
 */
export async function seedPostComments(
  queryClient: QueryClient,
  userId: string | undefined,
  postId: number,
  comments: WireComment[]
) {
  try {
    queryClient.setQueryData(
      getCommentsV2QueryOptions(userId, postId).queryKey,
      await toThread(comments, { failFast: true })
    );
  } catch {
    // The thread fetches them itself once the post is in.
  }
}
